import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import styled from 'styled-components';
import { Quote } from 'lucide-react';

interface CarouselItemData {
  title: string;
  description: string;
  id: number | string;
  icon?: React.ReactNode;
}

interface CarouselProps {
  items: CarouselItemData[];
  baseWidth?: number;
  autoplay?: boolean;
  autoplayDelay?: number;
  pauseOnHover?: boolean;
  loop?: boolean;
  round?: boolean;
}

const Container = styled.div`
  position: relative;
  overflow: hidden;
  padding: 40px 0;
  width: 100%;
  margin: 0 auto;
`;

const Track = styled(motion.div)`
  display: flex;
  align-items: center;
`;

const ItemWrapper = styled(motion.div)<{ width: number; round?: boolean }>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: ${props => props.round ? '50%' : '24px'};
  background-color: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  overflow: hidden;
  cursor: grab;
  width: ${props => props.width}px;
  height: ${props => props.round ? `${props.width}px` : '380px'};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);

  &:active {
    cursor: grabbing;
  }
`;

const ItemHeader = styled.div`
  padding: 30px;
`;

const IconContainer = styled.span`
  display: flex;
  height: 48px;
  width: 48px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background-color: #6bb05e;
  color: white;
  box-shadow: 0 8px 16px rgba(107, 176, 94, 0.3);
`;

const ItemContent = styled.div`
  padding: 30px;
  padding-top: 0;
`;

const ItemTitle = styled.div`
  margin-top: 1.5rem;
  font-weight: 800;
  font-size: 1.1rem;
  color: white;
  opacity: 0.9;
`;

const ItemDescription = styled.p`
  font-size: 1.2rem;
  color: white;
  line-height: 1.6;
  font-style: italic;
  margin: 0;
`;

const IndicatorsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 3rem;
`;

const Indicators = styled.div`
  display: flex;
  gap: 12px;
`;

const Indicator = styled(motion.div)<{ active: boolean }>`
  height: 6px;
  width: 24px;
  border-radius: 100px;
  cursor: pointer;
  background-color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.2)'};
  transition: background-color 0.3s;
`;

const DRAG_BUFFER = 50;
const VELOCITY_THRESHOLD = 500;
const GAP = 24;
const SPRING_OPTIONS = { type: 'spring', stiffness: 300, damping: 35, mass: 1 };

const CarouselItem: React.FC<{
  item: CarouselItemData;
  index: number;
  itemWidth: number;
  round?: boolean;
  trackItemOffset: number;
  x: any;
}> = ({ item, index, itemWidth, round, trackItemOffset, x }) => {
  const range = [-(index + 1) * trackItemOffset, -index * trackItemOffset, -(index - 1) * trackItemOffset];
  // Reduced rotation for better readability and less clipping
  const outputRange = [25, 0, -25];
  const rotateY = useTransform(x, range, outputRange, { clamp: false });
  const scale = useTransform(x, range, [0.9, 1, 0.9], { clamp: false });
  const opacity = useTransform(x, range, [0.5, 1, 0.5], { clamp: false });

  return (
    <ItemWrapper
      key={`${item.id}-${index}`}
      width={itemWidth}
      round={round}
      style={{
        rotateY: rotateY,
        scale: scale,
        opacity: opacity,
      }}
    >
      <ItemHeader>
        <IconContainer>
          {item.icon || <Quote size={24} />}
        </IconContainer>
      </ItemHeader>
      <ItemContent>
        <ItemDescription>"{item.description}"</ItemDescription>
        <ItemTitle>{item.title}</ItemTitle>
      </ItemContent>
    </ItemWrapper>
  );
};

export const Carousel: React.FC<CarouselProps> = ({
  items,
  baseWidth = 450,
  autoplay = false,
  autoplayDelay = 5000,
  pauseOnHover = false,
  loop = false,
  round = false
}) => {
  const itemWidth = baseWidth;
  const trackItemOffset = itemWidth + GAP;
  
  const itemsForRender = useMemo(() => {
    if (!loop) return items;
    if (items.length === 0) return [];
    return [items[items.length - 1], ...items, items[0]];
  }, [items, loop]);

  const [position, setPosition] = useState(loop ? 1 : 0);
  const x = useMotionValue(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pauseOnHover && containerRef.current) {
      const container = containerRef.current;
      const handleMouseEnter = () => setIsHovered(true);
      const handleMouseLeave = () => setIsHovered(false);
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [pauseOnHover]);

  useEffect(() => {
    if (!autoplay || itemsForRender.length <= 1) return undefined;
    if (pauseOnHover && isHovered) return undefined;

    const timer = setInterval(() => {
      setPosition(prev => {
        if (prev >= itemsForRender.length - 1) return loop ? 1 : prev;
        return prev + 1;
      });
    }, autoplayDelay);

    return () => clearInterval(timer);
  }, [autoplay, autoplayDelay, isHovered, pauseOnHover, itemsForRender.length, loop]);

  useEffect(() => {
    const startingPosition = loop ? 1 : 0;
    setPosition(startingPosition);
    x.set(-startingPosition * trackItemOffset);
  }, [items.length, loop, trackItemOffset, x]);

  const handleAnimationStart = () => setIsAnimating(true);
  const handleAnimationComplete = () => {
    setIsAnimating(false);
    
    if (!loop || itemsForRender.length <= 1) return;
    
    const lastCloneIndex = itemsForRender.length - 1;
    if (position === lastCloneIndex) {
      setPosition(1);
      x.set(-trackItemOffset);
    } else if (position === 0) {
      setPosition(items.length);
      x.set(-items.length * trackItemOffset);
    }
  };

  const handleDragEnd = (_: any, info: any) => {
    const { offset, velocity } = info;
    const direction =
      offset.x < -DRAG_BUFFER || velocity.x < -VELOCITY_THRESHOLD
        ? 1
        : offset.x > DRAG_BUFFER || velocity.x > VELOCITY_THRESHOLD
          ? -1
          : 0;

    if (direction === 0) return;

    setPosition(prev => {
      const next = prev + direction;
      const max = itemsForRender.length - 1;
      return Math.max(0, Math.min(next, max));
    });
  };

  const activeIndex =
    items.length === 0 ? 0 : loop ? (position - 1 + items.length) % items.length : Math.min(position, items.length - 1);

  // Center the track based on viewport
  const centerOffset = typeof window !== 'undefined' ? (window.innerWidth - itemWidth) / 2 : 0;

  return (
    <Container ref={containerRef}>
      <Track
        drag={isAnimating ? false : 'x'}
        style={{
          width: itemWidth,
          gap: `${GAP}px`,
          perspective: 1200,
          perspectiveOrigin: `${position * trackItemOffset + itemWidth / 2}px 50%`,
          x,
          marginLeft: centerOffset > 0 ? `${centerOffset}px` : '0'
        }}
        onDragEnd={handleDragEnd}
        animate={{ x: -(position * trackItemOffset) }}
        transition={SPRING_OPTIONS}
        onAnimationStart={handleAnimationStart}
        onAnimationComplete={handleAnimationComplete}
      >
        {itemsForRender.map((item, index) => (
          <CarouselItem
            key={`${item.id}-${index}`}
            item={item}
            index={index}
            itemWidth={itemWidth}
            round={round}
            trackItemOffset={trackItemOffset}
            x={x}
          />
        ))}
      </Track>
      <IndicatorsContainer>
        <Indicators>
          {items.map((_, index) => (
            <Indicator
              key={index}
              active={activeIndex === index}
              animate={{
                width: activeIndex === index ? 40 : 12,
                opacity: activeIndex === index ? 1 : 0.4
              }}
              onClick={() => setPosition(loop ? index + 1 : index)}
            />
          ))}
        </Indicators>
      </IndicatorsContainer>
    </Container>
  );
};
